import * as Dialog from '@radix-ui/react-dialog';
import React, { FC, HTMLAttributes, Ref } from 'react';
import { animated, useTransition, config as configMap, SpringConfig } from 'react-spring';
import styled, { css } from 'styled-components';
import { Sesame } from '../../hooks/useSesame';
import Cross from '../../svg/Cross';
import ArrowLeft from '../../svg/ArrowLeft';
import { UnreachableCaseError } from '../../entities/UnreachableCaseError';

export type ModalVariant = 'small' | 'regular' | 'large' | 'full';

export interface ModalProps extends HTMLAttributes<HTMLDivElement>, Pick<Sesame, 'open' | 'onClose'> {
    variant?: ModalVariant;
    config?: SpringConfig | keyof typeof configMap;
    contentRef?: Ref<HTMLDivElement>;
    dialogRef?: Ref<HTMLDivElement>;
    onBack?: () => void | null | undefined;
}

const Modal: FC<React.PropsWithChildren<ModalProps>> = ({
    children,
    open,
    onClose,
    variant = 'large',
    config = 'default',
    contentRef,
    dialogRef,
    onBack,
    ...props
}) => {
    const transitions = useTransition(open, {
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
        config: typeof config === 'string' ? configMap[config] : config,
    });

    return (
        <>
            {transitions(
                ({ opacity }, item) =>
                    item && (
                        <Dialog.Root open onOpenChange={isOpen => !isOpen && onClose()}>
                            <Dialog.Portal forceMount>
                                <AnimatedDialogOverlay forceMount style={{ opacity }} ref={dialogRef} />
                                <AnimatedDialogContent
                                    {...props}
                                    forceMount
                                    variant={variant}
                                    style={{ ...props.style, opacity }}
                                    aria-label="Modal"
                                    aria-describedby={undefined}
                                    ref={contentRef}
                                    onOpenAutoFocus={(event: Event) => event.preventDefault()}
                                >
                                    {onBack && (
                                        <BackButton onClick={onBack}>
                                            <ArrowLeft />
                                        </BackButton>
                                    )}
                                    <CloseButton onClick={onClose} aria-label="Close Modal">
                                        <Cross />
                                    </CloseButton>
                                    {children}
                                </AnimatedDialogContent>
                            </Dialog.Portal>
                        </Dialog.Root>
                    )
            )}
        </>
    );
};

export default Modal;

const CloseButton = styled.button`
    display: flex;
    position: absolute;
    padding: 0;
    top: 2.4rem;
    right: 2.4rem;
    height: 2.4rem;
    width: 2.4rem;
    > svg {
        margin: auto;
    }
    outline: none;
    z-index: ${({ theme }) => theme.zIndices.modal};
    &:hover {
        background: ${({ theme }) => theme.colors.neutral['5']};
    }
`;

const BackButton = styled.button`
    display: flex;
    position: absolute;
    padding: 0;
    top: 2.4rem;
    left: 2.4rem;
    height: 2.4rem;
    width: 2.4rem;
    > svg {
        margin: auto;
    }
    outline: none;
    z-index: ${({ theme }) => theme.zIndices.modal};
`;

const largeCss = css`
    width: calc(100vw - 4.8rem);
    max-width: ${({ theme }) => theme.mediaQueries.xxl};
    margin: auto;
    ${CloseButton} {
        > svg {
            transform: scale(1.4);
        }
    }
`;

const regularCss = css`
    width: 56rem;
`;

const smallCss = css`
    width: 34rem;
`;

const fullscreenCss = css`
    width: 100%;
    max-width: unset;
    max-height: unset;
    height: 100%;
    border-radius: 0;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        padding-top: 3.2rem;
        ${CloseButton} {
            right: 3.2rem;
            top: 3.2rem;
        }
    }
`;

const isEmbedPath = () => typeof window !== 'undefined' && window.location.pathname.includes('/embed/');

const getCss = (variant: ModalVariant) => {
    if (isEmbedPath()) {
        return fullscreenCss;
    }
    switch (variant) {
        case 'full':
            return fullscreenCss;
        case 'large':
            return largeCss;
        case 'regular':
            return regularCss;
        case 'small':
            return smallCss;
        default:
            throw new UnreachableCaseError(variant);
    }
};

const AnimatedDialogOverlay = animated(styled(Dialog.Overlay)`
    position: fixed;
    inset: 0;
    z-index: ${({ theme }) => theme.zIndices.modal};
    background: hsla(0, 0%, 0%, 0.33);
`);

export const AnimatedDialogContent = animated(styled(Dialog.Content)<Pick<ModalProps, 'variant'>>`
    position: fixed;
    inset: 0;
    z-index: ${({ theme }) => theme.zIndices.modal};
    overflow: auto;
    width: 100%;
    height: 100%;
    max-height: 100vh;
    padding: 2rem;
    background: ${({ theme }) => theme.colors.neutral['0']};
    outline: none;
    box-shadow: 0 1.6rem 2.4rem 0 rgba(59, 118, 160, 0.03), 0 2.4rem 8rem 0 rgba(59, 118, 160, 0.05);

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        inset: auto;
        left: 50%;
        top: 50%;
        height: auto;
        transform: translate(-50%, -50%);
        max-height: 90vh;
        max-width: 90vw;
        padding: ${({ variant = 'large' }) => (variant === 'regular' ? '3.2rem' : '2.4rem')};
        border-radius: 0.8rem;
        ${({ variant = 'large' }) => getCss(variant)}
    }
    ${() =>
        isEmbedPath() &&
        css`
            max-height: 100%;
            overflow: auto;
        `}
`);
